import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSync, faArrowLeft, faTrash, faUsers, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { getConsultantDetails, deleteConsultant } from '../../services/api';
import { format } from 'date-fns';

interface ConsultantData {
  id: string;
  name: string;
  email: string;
  created_at: string;
  client_count: number;
  recent_clients: ClientData[];
}

interface ClientData {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

const ConsultantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [consultant, setConsultant] = useState<ConsultantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    // Check if user is logged in and is a consultant
    const token = localStorage.getItem('auth_token');
    const userRole = localStorage.getItem('user_role');
    
    if (!token || userRole !== 'super') {
      console.log('[AUTH] No valid consultant token found, redirecting to login');
      navigate('/login');
      return;
    }
    
    if (id) {
      fetchConsultantDetails(id);
    } else {
      setError('No consultant ID provided');
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchConsultantDetails = async (consultantId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getConsultantDetails(consultantId);
      setConsultant(data);
    } catch (error: any) {
      console.error('Error fetching consultant details:', error);
      setError(error.message || 'Failed to load consultant details');
      toast.error(error.message || 'Failed to load consultant details');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleDeleteConsultant = async () => {
    if (!consultant) return;
    
    try {
      await deleteConsultant(consultant.id);
      toast.success('Consultant deleted successfully');
      navigate('/consultant/consultants');
    } catch (error: any) {
      console.error('Error deleting consultant:', error);
      toast.error(error.message || 'Failed to delete consultant');
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/consultant/consultants')}
          className="text-blue-600 hover:text-blue-800 flex items-center mr-6"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Consultants
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Consultant Details</h1>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <FontAwesomeIcon icon={faSync} spin className="text-blue-500 mr-2" size="lg" />
          <p className="text-gray-600">Loading consultant details...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => id && fetchConsultantDetails(id)}
                className="mt-2 text-sm text-red-700 hover:text-red-600 font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      ) : consultant ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Consultant Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex flex-col items-center mb-6">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-4xl mb-4">
                  {consultant.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{consultant.name}</h2>
                <p className="text-gray-500">{consultant.email}</p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 w-5 h-5 mr-3" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Joined On</p>
                    <p className="text-gray-700">{format(new Date(consultant.created_at), 'MMMM d, yyyy')}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faUsers} className="text-gray-400 w-5 h-5 mr-3" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Total Clients</p>
                    <p className="text-gray-700">{consultant.client_count}</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={openDeleteModal}
                  className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={consultant.client_count > 0}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  Delete Consultant
                </button>
                {consultant.client_count > 0 && (
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Cannot delete while consultant has active clients. Reassign or delete clients first.
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Recent Clients Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Clients</h3>
              
              {consultant.recent_clients && consultant.recent_clients.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {consultant.recent_clients.map((client) => (
                        <tr key={client.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600">
                                {client.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {client.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {client.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(client.created_at), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faUsers} className="text-gray-300 text-4xl mb-3" />
                  <p className="text-gray-500">No clients found for this consultant</p>
                </div>
              )}
              
              {consultant.client_count > consultant.recent_clients.length && (
                <div className="mt-4 text-right">
                  <p className="text-sm text-gray-500">
                    Showing {consultant.recent_clients.length} of {consultant.client_count} total clients
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16">
          <FontAwesomeIcon icon={faUser} className="text-gray-300 text-4xl mb-3" />
          <p className="text-gray-500">Consultant not found</p>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && consultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Consultant</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete consultant <span className="font-semibold">{consultant.name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConsultant}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantDetails; 